import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { LoadOrgStatus } from '@/modules/auth/use-cases'
import { canUseCommandCenter, hasNonManagerOrgAccess } from '@/shared/domain'
import { useAuth, useForceTheme } from '@/shared/ui/hooks'

export type UseLandingPageProps = {
  onLoadOrgStatus: LoadOrgStatus
}

export type UseLandingPageResult =
  | { status: 'loading' }
  | { status: 'redirecting' }
  | { status: 'unauthenticated' }

export function useLandingPage({
  onLoadOrgStatus,
}: UseLandingPageProps): UseLandingPageResult {
  const { isLoading, user } = useAuth()
  const navigate = useNavigate()
  const hasRunRef = useRef<boolean>(false)
  const [status, setStatus] = useState<UseLandingPageResult['status']>('loading')

  useForceTheme('dark')

  useEffect(() => {
    if (isLoading) return
    if (!user) {
      setStatus('unauthenticated')
      return
    }
    if (hasRunRef.current) return
    hasRunRef.current = true
    setStatus('redirecting')

    const run = async () => {
      try {
        const orgStatus = await onLoadOrgStatus()
        if (canUseCommandCenter(orgStatus) || orgStatus.hasPendingOwnerInvitations) {
          navigate('/dashboard', { replace: true })
          return
        }
        navigate(hasNonManagerOrgAccess(orgStatus) ? '/no-access' : '/register', { replace: true })
      } catch {
        setStatus('unauthenticated')
      }
    }

    void run()
  }, [isLoading, user, navigate, onLoadOrgStatus])

  return { status }
}
